import { type QueryClient } from '@tanstack/react-query'
import { ReadWorkflowsV3 } from '../../util/operationIds'
import {
  filterIdsOfKey,
  refreshCachedEntityDocuments,
} from '../../util/refreshCachedEntityDocuments'
import { replaceEntityWorkflows } from './cacheTransformFns/replaceEntityWorkflows'
import { readEntityWorkflows } from './readEntityWorkflows'
import { type EntityWorkflowsDocument, type WorkflowEntityType } from './types'

const filterNameOf = (entityType: WorkflowEntityType) =>
  `context.${entityType}_id`

/**
 * Re-reads a block's or moment's workflows into every cached v3 workflows
 * document that covers it. Short-circuits if there are no such documents. If
 * the read fails, the documents are invalidated instead of the promise failing.
 */
export const refreshEntityWorkflows = (
  queryClient: QueryClient,
  entityType: WorkflowEntityType,
  entityId: string
) =>
  refreshCachedEntityDocuments<EntityWorkflowsDocument>({
    entityId,
    filterName: filterNameOf(entityType),
    operationId: ReadWorkflowsV3,
    queryClient,
    read: () => readEntityWorkflows(entityType, [entityId]),
    replace: (incoming) =>
      replaceEntityWorkflows(entityType, [entityId], incoming),
    resourceKey: 'workflow',
  })

/**
 * Marks every cached v3 workflows document of `entityType` stale, for a write
 * whose entity UUID is unknown (Plasma has only a moment's legacy id).
 */
export const invalidateEntityWorkflows = (
  queryClient: QueryClient,
  entityType: WorkflowEntityType
) =>
  queryClient.invalidateQueries({
    queryKey: ['workflow'],
    predicate: ({ queryKey }) =>
      filterIdsOfKey(queryKey, ReadWorkflowsV3, filterNameOf(entityType)) !==
      undefined,
  })
